import { Link } from '@inertiajs/react'

import AdminLayout from '../../../components/AdminLayout'
import { AdminCard, AdminHead } from '../../../components/admin/AdminHead'
import { OrderStatus } from '../../../components/admin/OrderStatus'
import { ImagePlaceholderIcon } from '../../../components/Icons'
import { Badge } from '@/components/ui/badge'
import { StatTile } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface Product {
  id: number
  name: string
  price: number
  stock: number
  image_url: string | null
}

interface Order {
  id: number
  customer_name: string
  customer_email: string
  total: number
  status: string
  items_count: number
  created_at: string
}

interface Stats {
  products_count: number
  orders_count: number
  total_revenue: number
}

interface Props {
  stats: Stats
  products: Product[]
  orders: Order[]
}

/** Spec 11 · Stat tiles over the two recent-activity tables. */
export default function AdminDashboardIndex({ stats, products, orders }: Props) {
  const outOfStock = products.filter((product) => product.stock === 0).length

  return (
    <AdminLayout>
      <AdminHead eyebrow="Overview" title="Dashboard" />

      <div className="mb-6 grid grid-cols-4 gap-3 max-[919px]:grid-cols-2 max-[559px]:grid-cols-1">
        <StatTile label="Revenue" value={`$${stats.total_revenue.toFixed(2)}`} />
        <StatTile label="Orders" value={stats.orders_count} />
        <StatTile label="Products" value={stats.products_count} />
        <StatTile
          label="Out of stock"
          value={outOfStock}
          delta={outOfStock > 0 ? 'Needs restock' : 'All stocked'}
          flagged={outOfStock > 0}
        />
      </div>

      <AdminCard
        title="Recent products"
        action={
          <Link
            href="/admin/products"
            className="text-[13px] font-medium text-ink-muted hover:text-signal-deep"
          >
            View all →
          </Link>
        }
      >
        {products.length === 0 ? (
          <div className="p-[18px]">
            <EmptyState title="No products yet." />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-surface-sunk">
                <TableHead>Product</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id} flagged={product.stock === 0}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span className="size-9 shrink-0 overflow-hidden rounded-sm bg-surface-sunk">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="size-full object-cover"
                          />
                        ) : (
                          <span className="flex size-full items-center justify-center text-ink-faint">
                            <ImagePlaceholderIcon size={16} />
                          </span>
                        )}
                      </span>
                      {product.name}
                    </div>
                  </TableCell>
                  <TableCell className="tabular-nums">${product.price.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant={product.stock === 0 ? 'accent' : 'outline'}>
                      {product.stock === 0 ? 'Sold out' : `${product.stock} in stock`}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Link
                      href={`/admin/products/${product.id}/edit`}
                      className="text-[13px] font-medium underline underline-offset-4 hover:text-signal-deep"
                    >
                      Edit
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminCard>

      <AdminCard
        className="mt-4"
        title="Recent orders"
        action={
          <Link
            href="/admin/orders"
            className="text-[13px] font-medium text-ink-muted hover:text-signal-deep"
          >
            View all →
          </Link>
        }
      >
        {orders.length === 0 ? (
          <div className="p-[18px]">
            <EmptyState title="No orders yet." />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-surface-sunk">
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} flagged={order.status === 'pending'}>
                  <TableCell className="tabular-nums">
                    #SS-{String(order.id).padStart(4, '0')}
                  </TableCell>
                  <TableCell>
                    <div>{order.customer_name}</div>
                    <div className="text-[11px] text-ink-muted">{order.customer_email}</div>
                  </TableCell>
                  <TableCell>
                    <OrderStatus status={order.status} />
                  </TableCell>
                  <TableCell className="text-ink-muted">{order.items_count} items</TableCell>
                  <TableCell className="text-right tabular-nums">
                    ${order.total.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link
                      href={`/admin/orders/${order.id}`}
                      className="text-[13px] font-medium underline underline-offset-4 hover:text-signal-deep"
                    >
                      View
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminCard>
    </AdminLayout>
  )
}
